// src/components/SalaryApprovalsDashboard.js
// Complete version with employee search, selectable deductions, and editable amounts
// FIXED: Approved bonuses tab now displays correctly
// FIXED: Request date formatting for all tabs

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  TablePagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Comment as CommentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CardGiftcard as BonusIcon,
  Paid as PaidIcon,
  Info as InfoIcon,
  ReceiptLong as RegisterIcon,
  AccountBalance as TaxIcon,
  ExpandMore as ExpandMoreIcon,
  Calculate as CalculateIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip as RechartsTooltip,
} from 'recharts';

import Sidebar from '../components/sidebars/Sidebar_accountant';
import TopNav from '../components/top_bars/TopNav_staff';

// ==================== DEBUG UTILITIES ====================
const DEBUG = true;

function debugLog(message, data = null) {
  if (DEBUG) {
    const timestamp = new Date().toISOString();
    if (data) {
      console.log(`[${timestamp}] [SalaryApprovals] ${message}`, data);
    } else {
      console.log(`[${timestamp}] [SalaryApprovals] ${message}`);
    }
  }
}

function debugError(message, error = null) {
  const timestamp = new Date().toISOString();
  if (error) {
    console.error(`[${timestamp}] [SalaryApprovals ERROR] ${message}`, error);
  } else {
    console.error(`[${timestamp}] [SalaryApprovals ERROR] ${message}`);
  }
}

const COLORS = ['#ff9800', '#4caf50', '#f44336', '#2196f3', '#9c27b0'];

// ==================== HELPER FUNCTIONS ====================
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '₦0.00';
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) return '₦0.00';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numAmount);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    debugError('Date formatting error:', e);
    return 'Invalid Date';
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    debugError('DateTime formatting error:', e);
    return 'Invalid Date';
  }
};

const sanitizeInput = (input) => {
  if (!input) return '';
  if (typeof input !== 'string') return input;
  return input
    .replace(/[<>]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .trim();
};

const SalaryApprovalsDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);

  // Request data
  const [pendingDeductions, setPendingDeductions] = useState([]);
  const [pendingBonuses, setPendingBonuses] = useState([]);
  const [pendingSalaryChanges, setPendingSalaryChanges] = useState([]);
  const [approvedBonuses, setApprovedBonuses] = useState([]);
  const [deductionRegister, setDeductionRegister] = useState([]);
  const [registerStats, setRegisterStats] = useState(null);
  
  // Statutory deductions
  const [availableDeductions, setAvailableDeductions] = useState([]);
  const [calculatedStatutory, setCalculatedStatutory] = useState(null);
  const [selectedDeductionIds, setSelectedDeductionIds] = useState([]);
  const [customDeductionAmounts, setCustomDeductionAmounts] = useState({});
  
  // Employee search
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [employeeSearchResults, setEmployeeSearchResults] = useState([]);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const [calculatingDeductions, setCalculatingDeductions] = useState(false);
  
  // Stats
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registerLoading, setRegisterLoading] = useState(false);
  
  // UI State
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [statutoryDialogOpen, setStatutoryDialogOpen] = useState(false);
  const [statutoryEmployee, setStatutoryEmployee] = useState(null);
  const [statutoryMonth, setStatutoryMonth] = useState(new Date().getMonth() + 1);
  const [statutoryYear, setStatutoryYear] = useState(new Date().getFullYear());
  const [applyToAll, setApplyToAll] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [requestTypeFilter, setRequestTypeFilter] = useState('all');
  const [registerTypeFilter, setRegisterTypeFilter] = useState('all');
  const [registerStatusFilter, setRegisterStatusFilter] = useState('all');
  const [registerMonth, setRegisterMonth] = useState(new Date().getMonth() + 1);
  const [registerYear, setRegisterYear] = useState(new Date().getFullYear());
  const [departments, setDepartments] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Current user
  const [currentUser, setCurrentUser] = useState({
    name: '',
    role: '',
    email: ''
  });

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Search employees with debounce
  const searchEmployees = useCallback(async (searchTerm) => {
    if (searchTerm.length < 2) {
      setEmployeeSearchResults([]);
      return;
    }
    
    setEmployeeSearchLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/statutory_deductions.php?action=search_employees&search=${encodeURIComponent(searchTerm)}`);
      const result = await response.json();
      
      if (result.success) {
        setEmployeeSearchResults(result.data);
        debugLog(`Search found ${result.data.length} employees`, { searchTerm });
      } else {
        debugError('Employee search failed:', result.message);
      }
    } catch (err) {
      debugError('Error searching employees:', err);
    } finally {
      setEmployeeSearchLoading(false);
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (employeeSearchTerm) {
        searchEmployees(employeeSearchTerm);
      } else {
        setEmployeeSearchResults([]);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [employeeSearchTerm, searchEmployees]);

  // Fetch available deductions when dialog opens
  useEffect(() => {
    if (statutoryDialogOpen) {
      fetchAvailableDeductions();
    }
  }, [statutoryDialogOpen]);

  // Calculate deductions when employee or selections change
  useEffect(() => {
    if (statutoryEmployee?.id && selectedDeductionIds.length > 0) {
      calculateSelectedDeductions();
    }
  }, [statutoryEmployee, selectedDeductionIds]);

  const fetchAllData = async () => {
    debugLog('Fetching all dashboard data...');
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      
      // Fetch pending deductions
      debugLog('Fetching pending deductions...');
      const deductionsRes = await fetch(`${baseUrl}/salary_deductions.php?type=pending`);
      const deductionsResult = await deductionsRes.json();
      if (deductionsResult.success) {
        setPendingDeductions(deductionsResult.data);
        debugLog(`Loaded ${deductionsResult.data.length} pending deductions`);
      } else {
        debugError('Failed to fetch pending deductions:', deductionsResult.message);
      }
      
      // Fetch pending bonuses
      debugLog('Fetching pending bonuses...');
      const bonusesRes = await fetch(`${baseUrl}/update_salary.php?approval_status=pending&request_type=bonus`);
      const bonusesResult = await bonusesRes.json();
      if (bonusesResult.success) {
        setPendingBonuses(bonusesResult.data);
        debugLog(`Loaded ${bonusesResult.data.length} pending bonuses`);
      } else {
        debugError('Failed to fetch pending bonuses:', bonusesResult.message);
      }
      
      // Fetch pending salary changes
      debugLog('Fetching pending salary changes...');
      const salaryChangesRes = await fetch(`${baseUrl}/update_salary.php?approval_status=pending&request_type=all`);
      const salaryChangesResult = await salaryChangesRes.json();
      if (salaryChangesResult.success) {
        const changes = salaryChangesResult.data.filter(item => 
          (!item.is_bonus || item.is_bonus === 0) && 
          (item.recent_increment > 0 || item.recent_downgrade > 0)
        );
        setPendingSalaryChanges(changes);
        debugLog(`Loaded ${changes.length} pending salary changes`);
      } else {
        debugError('Failed to fetch pending salary changes:', salaryChangesResult.message);
      }
      
      // Fetch approved but unpaid bonuses - FIXED: Use correct endpoint and field names
      debugLog('Fetching approved bonuses...');
      const approvedBonusesRes = await fetch(`${baseUrl}/update_salary.php?approval_status=approved&request_type=bonus`);
      const approvedBonusesResult = await approvedBonusesRes.json();
      if (approvedBonusesResult.success) {
        // Handle different possible field name variations from API
        const unpaid = approvedBonusesResult.data.filter(b => {
          const isUnpaid = b.is_paid === 0 || b.is_paid === null || b.bonus_paid === 0 || b.bonus_paid === null;
          debugLog(`Bonus ${b.id} for ${b.full_name}: is_paid=${b.is_paid}, bonus_paid=${b.bonus_paid}, isUnpaid=${isUnpaid}`);
          return isUnpaid;
        });
        setApprovedBonuses(unpaid);
        debugLog(`Loaded ${approvedBonusesResult.data.length} approved bonuses, ${unpaid.length} unpaid`);
      } else {
        debugError('Failed to fetch approved bonuses:', approvedBonusesResult.message);
      }
      
      // Fetch stats
      debugLog('Fetching stats...');
      const statsRes = await fetch(`${baseUrl}/update_salary.php?type=stats`);
      const statsResult = await statsRes.json();
      if (statsResult.success) {
        setStats(statsResult.stats);
        debugLog('Stats loaded:', statsResult.stats);
      } else {
        debugError('Failed to fetch stats:', statsResult.message);
      }
      
      // Fetch deduction register
      await fetchDeductionRegister();
      
    } catch (err) {
      debugError('Error fetching data:', err);
      setSnackbar({ open: true, message: 'Failed to load data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchDeductionRegister = async () => {
    debugLog(`Fetching deduction register for ${registerMonth}/${registerYear}...`);
    setRegisterLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/statutory_deductions.php?action=get_deduction_register&month=${registerMonth}&year=${registerYear}&type=${registerTypeFilter}&status=${registerStatusFilter}`);
      const result = await response.json();
      
      if (result.success) {
        setDeductionRegister(result.data);
        setRegisterStats(result.stats);
        debugLog(`Loaded ${result.data.length} register entries`, result.stats);
      } else {
        debugError('Failed to fetch deduction register:', result.message);
      }
    } catch (err) {
      debugError('Error fetching deduction register:', err);
    } finally {
      setRegisterLoading(false);
    }
  };

  const fetchAvailableDeductions = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/statutory_deductions.php?action=get_available_deductions`);
      const result = await response.json();
      
      if (result.success) {
        setAvailableDeductions(result.data);
        // Auto-select mandatory deductions
        const mandatoryIds = result.data.filter(d => d.is_mandatory === 1).map(d => d.id);
        setSelectedDeductionIds(mandatoryIds);
        debugLog(`Loaded ${result.data.length} available deductions, ${mandatoryIds.length} mandatory`);
      } else {
        debugError('Failed to fetch available deductions:', result.message);
      }
    } catch (err) {
      debugError('Error fetching available deductions:', err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/get_depts_list.php`);
      const result = await response.json();
      if (result.success) {
        setDepartments(result.data);
        debugLog(`Loaded ${result.data.length} departments`);
      }
    } catch (err) {
      debugError('Error fetching departments:', err);
    }
  };

  const calculateSelectedDeductions = async () => {
    if (!statutoryEmployee?.id) return;
    
    debugLog(`Calculating deductions for employee ${statutoryEmployee.id}`, { selectedDeductionIds });
    setCalculatingDeductions(true);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/statutory_deductions.php?action=calculate_for_employee&employee_id=${statutoryEmployee.id}&selected_deductions=${JSON.stringify(selectedDeductionIds)}`);
      const result = await response.json();
      
      if (result.success) {
        setCalculatedStatutory(result.data);
        // Initialize custom amounts with calculated values
        const initialCustomAmounts = {};
        result.data.statutory_deductions.forEach(ded => {
          initialCustomAmounts[ded.id] = ded.amount;
        });
        setCustomDeductionAmounts(initialCustomAmounts);
        debugLog('Deductions calculated', { total: result.data.total_statutory_deductions });
      } else {
        debugError('Failed to calculate deductions:', result.message);
        setSnackbar({ open: true, message: result.message, severity: 'error' });
      }
    } catch (err) {
      debugError('Error calculating deductions:', err);
      setSnackbar({ open: true, message: 'Failed to calculate deductions', severity: 'error' });
    } finally {
      setCalculatingDeductions(false);
    }
  };

  const toggleDeductionSelection = (deductionId) => {
    setSelectedDeductionIds(prev => {
      if (prev.includes(deductionId)) {
        return prev.filter(id => id !== deductionId);
      } else {
        return [...prev, deductionId];
      }
    });
  };

  const updateCustomDeductionAmount = (deductionId, amount) => {
    const newAmount = parseFloat(amount) || 0;
    setCustomDeductionAmounts(prev => ({
      ...prev,
      [deductionId]: newAmount
    }));
    
    // Update calculated statutory data
    if (calculatedStatutory?.statutory_deductions) {
      const updatedDeductions = calculatedStatutory.statutory_deductions.map(ded => {
        if (ded.id === deductionId) {
          return { ...ded, amount: newAmount };
        }
        return ded;
      });
      
      const newTotal = updatedDeductions.reduce((sum, ded) => sum + ded.amount, 0);
      setCalculatedStatutory({
        ...calculatedStatutory,
        statutory_deductions: updatedDeductions,
        total_statutory_deductions: newTotal,
        net_after_statutory: calculatedStatutory.monthly_salary - newTotal
      });
    }
  };

  const applySelectedStatutoryDeductions = async () => {
    if (!applyToAll && !statutoryEmployee) {
      setSnackbar({ open: true, message: 'Please select an employee', severity: 'error' });
      return;
    }
    
    if (selectedDeductionIds.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one deduction to apply', severity: 'error' });
      return;
    }
    
    debugLog('Applying statutory deductions...', { applyToAll, statutoryEmployee, month: statutoryMonth, year: statutoryYear });
    setCalculatingDeductions(true);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const selectedDeductionsWithAmounts = selectedDeductionIds.map(id => ({
        id: id,
        custom_amount: customDeductionAmounts[id] || null
      }));
      
      const payload = {
        month: statutoryMonth,
        year: statutoryYear,
        apply_to_all: applyToAll,
        selected_deductions: selectedDeductionsWithAmounts
      };
      
      if (!applyToAll && statutoryEmployee) {
        payload.employee_id = statutoryEmployee.id;
        payload.phone = statutoryEmployee.phone;
      }
      
      const response = await fetch(`${baseUrl}/statutory_deductions.php?action=apply_statutory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      
      if (result.success) {
        if (result.warnings && result.warnings.length > 0) {
          setValidationWarnings(result.warnings);
          setSnackbar({ 
            open: true, 
            message: `Applied with warnings: ${result.warning_count} employee(s) have deductions > 90% of salary`, 
            severity: 'warning' 
          });
        } else {
          setSnackbar({ open: true, message: result.message, severity: 'success' });
        }
        
        setStatutoryDialogOpen(false);
        setStatutoryEmployee(null);
        setEmployeeSearchTerm('');
        setEmployeeSearchResults([]);
        setSelectedDeductionIds([]);
        setCustomDeductionAmounts({});
        setCalculatedStatutory(null);
        fetchDeductionRegister();
        fetchAllData();
      } else {
        if (result.errors && result.errors.length > 0) {
          setValidationErrors(result.errors);
          const errorMessage = result.errors.map(e => e.message).join('\n');
          setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        } else {
          setSnackbar({ open: true, message: result.message, severity: 'error' });
        }
      }
    } catch (err) {
      debugError('Error applying statutory deductions:', err);
      setSnackbar({ open: true, message: 'Failed to apply statutory deductions', severity: 'error' });
    } finally {
      setCalculatingDeductions(false);
    }
  };

  const handleApproveDeduction = async () => {
    debugLog(`Approving deduction ${selectedRequest?.id}`);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/salary_deductions.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selectedRequest.id,
          action: 'approve',
          approved_by: currentUser.name,
          rejection_reason: null
        }),
      });
      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setApproveDialogOpen(false);
        setApprovalComment('');
        fetchAllData();
      } else {
        setSnackbar({ open: true, message: result.message, severity: 'error' });
      }
    } catch (err) {
      debugError('Error approving deduction:', err);
      setSnackbar({ open: true, message: 'Failed to approve deduction', severity: 'error' });
    }
  };

  const handleApproveBonus = async () => {
    debugLog(`Approving bonus ${selectedRequest?.id}`);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/update_salary.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selectedRequest.id,
          approval_status: 'approved',
          approved_by: currentUser.name,
          approval_comment: approvalComment
        }),
      });
      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setApproveDialogOpen(false);
        setApprovalComment('');
        fetchAllData();
      } else {
        setSnackbar({ open: true, message: result.message, severity: 'error' });
      }
    } catch (err) {
      debugError('Error approving bonus:', err);
      setSnackbar({ open: true, message: 'Failed to approve bonus', severity: 'error' });
    }
  };

  const handleApproveSalaryChange = async () => {
    debugLog(`Approving salary change ${selectedRequest?.id}`);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/update_salary.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selectedRequest.id,
          approval_status: 'approved',
          approved_by: currentUser.name,
          approval_comment: approvalComment
        }),
      });
      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setApproveDialogOpen(false);
        setApprovalComment('');
        fetchAllData();
      } else {
        setSnackbar({ open: true, message: result.message, severity: 'error' });
      }
    } catch (err) {
      debugError('Error approving salary change:', err);
      setSnackbar({ open: true, message: 'Failed to approve request', severity: 'error' });
    }
  };

  const handleRejectRequest = async () => {
    if (!approvalComment && selectedRequest?.type !== 'deduction') {
      setSnackbar({ open: true, message: 'Please provide a reason for rejection', severity: 'error' });
      return;
    }

    debugLog(`Rejecting request ${selectedRequest?.id}, type: ${selectedRequest?.type}`);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      let url = '';
      let body = {};

      if (selectedRequest.type === 'deduction') {
        url = `${baseUrl}/salary_deductions.php`;
        body = {
          id: selectedRequest.id,
          action: 'reject',
          approved_by: currentUser.name,
          rejection_reason: approvalComment
        };
      } else {
        url = `${baseUrl}/update_salary.php`;
        body = {
          id: selectedRequest.id,
          approval_status: 'rejected',
          approved_by: currentUser.name,
          approval_comment: approvalComment
        };
      }

      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setRejectDialogOpen(false);
        setApprovalComment('');
        fetchAllData();
      } else {
        setSnackbar({ open: true, message: result.message, severity: 'error' });
      }
    } catch (err) {
      debugError('Error rejecting request:', err);
      setSnackbar({ open: true, message: 'Failed to reject request', severity: 'error' });
    }
  };

  const handleMarkBonusPaid = async (bonus) => {
    debugLog(`Marking bonus ${bonus.id} as paid for ${bonus.full_name}`);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/update_salary.php`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: bonus.id,
          paid_by: currentUser.name
        }),
      });
      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        fetchAllData();
      } else {
        setSnackbar({ open: true, message: result.message, severity: 'error' });
      }
    } catch (err) {
      debugError('Error marking bonus paid:', err);
      setSnackbar({ open: true, message: 'Failed to mark bonus as paid', severity: 'error' });
    }
  };

  const getRequestTypeConfig = (request) => {
    if (request.type === 'deduction') {
      return {
        label: 'Deduction',
        icon: <TrendingDownIcon />,
        color: '#f44336',
        bgColor: '#ffebee',
        amount: request.amount,
        reason: request.remark,
        effect: 'Deducted from salary',
      };
    } else if (request.type === 'bonus') {
      return {
        label: 'Bonus',
        icon: <BonusIcon />,
        color: '#ff9800',
        bgColor: '#fff3e0',
        amount: request.recent_bonus || request.bonus_amount,
        reason: request.bonus_reason || request.reason,
        effect: 'One-time payment (does not affect regular salary)',
      };
    } else if (request.type === 'increment') {
      return {
        label: 'Salary Increment',
        icon: <TrendingUpIcon />,
        color: '#4caf50',
        bgColor: '#e8f5e9',
        amount: request.recent_increment || request.amount,
        reason: request.increment_reason || request.reason,
        effect: 'Permanent salary increase',
      };
    } else {
      return {
        label: 'Salary Downgrade',
        icon: <TrendingDownIcon />,
        color: '#f44336',
        bgColor: '#ffebee',
        amount: request.recent_downgrade || request.amount,
        reason: request.downgrade_reason || request.reason,
        effect: 'Permanent salary reduction',
      };
    }
  };

  const getDeductionCategoryConfig = (category) => {
    const configs = {
      regular: { label: 'Regular Deduction', icon: <TrendingDownIcon />, color: '#f44336', bgColor: '#ffebee' },
      bonus: { label: 'Bonus', icon: <BonusIcon />, color: '#ff9800', bgColor: '#fff3e0' },
      statutory: { label: 'Statutory Deduction', icon: <TaxIcon />, color: '#2196f3', bgColor: '#e3f2fd' }
    };
    return configs[category] || configs.regular;
  };

  const getAllPendingRequests = () => {
    const deductions = pendingDeductions.map(d => ({ ...d, type: 'deduction', date_created: d.date_created }));
    const bonuses = pendingBonuses.map(b => ({ ...b, type: 'bonus', date_created: b.request_date || b.date_created }));
    const salaryChanges = pendingSalaryChanges.map(s => ({ 
      ...s, 
      type: s.is_downgrade ? 'downgrade' : 'increment',
      date_created: s.request_date || s.date_created
    }));
    
    let all = [...deductions, ...bonuses, ...salaryChanges];
    
    if (requestTypeFilter !== 'all') {
      all = all.filter(r => r.type === requestTypeFilter);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      all = all.filter(r => 
        (r.full_name || '').toLowerCase().includes(term) ||
        (r.department || '').toLowerCase().includes(term) ||
        (r.phone || '').includes(searchTerm)
      );
    }
    
    if (departmentFilter !== 'all') {
      all = all.filter(r => r.department === departmentFilter);
    }
    
    return all;
  };

  const getFilteredRegister = () => {
    let filtered = [...deductionRegister];
    
    if (registerTypeFilter !== 'all') {
      filtered = filtered.filter(r => r.deduction_category === registerTypeFilter);
    }
    
    if (registerStatusFilter !== 'all') {
      if (registerStatusFilter === 'approved') {
        filtered = filtered.filter(r => r.deduction_status === 1 || r.deduction_status === 'approved');
      } else if (registerStatusFilter === 'pending') {
        filtered = filtered.filter(r => r.deduction_status === 0 || r.deduction_status === 'pending');
      } else if (registerStatusFilter === 'rejected') {
        filtered = filtered.filter(r => r.deduction_status === 2 || r.deduction_status === 'rejected');
      }
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        (r.full_name || '').toLowerCase().includes(term) ||
        (r.department || '').toLowerCase().includes(term) ||
        (r.remark || '').toLowerCase().includes(term)
      );
    }
    
    return filtered;
  };

  const filteredRequests = getAllPendingRequests();
  const filteredRegister = getFilteredRegister();
  const totalPendingAmount = filteredRequests.reduce((sum, req) => {
    const config = getRequestTypeConfig(req);
    const amount = config.amount || 0;
    return sum + (typeof amount === 'number' ? amount : 0);
  }, 0);

  const pieChartData = [
    { name: 'Deductions', value: pendingDeductions.length, color: '#f44336' },
    { name: 'Bonuses', value: pendingBonuses.length, color: '#ff9800' },
    { name: 'Increments', value: pendingSalaryChanges.filter(s => !s.is_downgrade).length, color: '#4caf50' },
    { name: 'Downgrades', value: pendingSalaryChanges.filter(s => s.is_downgrade).length, color: '#f44336' },
  ].filter(item => item.value > 0);

  const StatCard = ({ title, value, subtitle, color, icon: Icon }) => (
    <Card sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="textSecondary">{title}</Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>{value}</Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 48, height: 48 }}>
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  useEffect(() => {
    const userName = localStorage.getItem('userName') || localStorage.getItem('name') || 'Accountant';
    const userRole = localStorage.getItem('role') || localStorage.getItem('userType') || 'accountant';
    const userEmail = localStorage.getItem('email') || localStorage.getItem('userEmail') || '';
    
    setCurrentUser({
      name: userName,
      role: userRole,
      email: userEmail
    });
    
    debugLog('Component initialized', { user: userName, role: userRole });
    fetchAllData();
    fetchDepartments();
  }, []);

  if (loading && filteredRequests.length === 0) {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box sx={{ flexGrow: 1, p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading pending approvals...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, maxWidth: '1400px', mx: 'auto', width: '100%', overflow: 'auto', height: '100vh' }}>
        <TopNav
          onLogout={() => {
            debugLog('User logging out');
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
            localStorage.removeItem('userName');
            localStorage.removeItem('userEmail');
            localStorage.removeItem('role');
          }}
          onNotificationClick={() => {}}
          toggleSidebar={toggleSidebar}
        />

        {/* Header */}
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, background: 'linear-gradient(135deg, #1B5E20 0%, #0D3B0F 100%)', color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold">Salary Approvals Dashboard</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Review and approve deductions, bonuses, and salary adjustments
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7, mt: 0.5, display: 'block' }}>
                Logged in as: <strong>{currentUser.name}</strong> ({currentUser.role})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="contained" 
                startIcon={<CalculateIcon />} 
                onClick={() => setStatutoryDialogOpen(true)}
                sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#ed6c02' } }}
              >
                Apply Statutory Deductions
              </Button>
              <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchAllData} sx={{ bgcolor: 'white', color: '#1B5E20', '&:hover': { bgcolor: '#f5f5f5' } }}>
                Refresh
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Statistics Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Total Pending" 
              value={filteredRequests.length} 
              color="#ff9800" 
              icon={PendingIcon}
              subtitle={`${formatCurrency(totalPendingAmount)} total`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Pending Deductions" 
              value={pendingDeductions.length} 
              color="#f44336" 
              icon={TrendingDownIcon}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Pending Bonuses" 
              value={pendingBonuses.length} 
              color="#ff9800" 
              icon={BonusIcon}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Pending Salary Changes" 
              value={pendingSalaryChanges.length} 
              color="#2196f3" 
              icon={MoneyIcon}
            />
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom>Pending Requests by Type</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom>Unpaid Bonuses</Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Approved Bonuses Awaiting Payment</Typography>
                  <Typography variant="h6" fontWeight="bold" color="#ff9800">{approvedBonuses.length}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Total Unpaid Amount</Typography>
                  <Typography variant="h6" fontWeight="bold" color="#ff9800">
                    {formatCurrency(approvedBonuses.reduce((sum, b) => sum + (b.recent_bonus || b.bonus_amount || 0), 0))}
                  </Typography>
                </Box>
                {approvedBonuses.length > 0 && (
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<PaidIcon />}
                    onClick={() => setActiveTab(1)}
                  >
                    View Unpaid Bonuses
                  </Button>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ mb: 3, borderRadius: 3 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="fullWidth">
            <Tab icon={<PendingIcon />} iconPosition="start" label={`All Pending (${filteredRequests.length})`} />
            <Tab icon={<CheckCircleIcon />} iconPosition="start" label={`Approved Bonuses (${approvedBonuses.length})`} />
            <Tab icon={<RegisterIcon />} iconPosition="start" label={`Deduction / Bonus Register (${deductionRegister.length})`} />
          </Tabs>
        </Paper>

        {/* Filters for Pending Tab */}
        {activeTab === 0 && (
          <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search by name, department, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ flex: 2, minWidth: 200 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Request Type</InputLabel>
                <Select value={requestTypeFilter} onChange={(e) => setRequestTypeFilter(e.target.value)} label="Request Type">
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="deduction">Deductions</MenuItem>
                  <MenuItem value="bonus">Bonuses</MenuItem>
                  <MenuItem value="increment">Increments</MenuItem>
                  <MenuItem value="downgrade">Downgrades</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Department</InputLabel>
                <Select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} label="Department">
                  <MenuItem value="all">All Departments</MenuItem>
                  {departments.map((dept, idx) => (
                    <MenuItem key={idx} value={dept.departments || dept}>{dept.departments || dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>
        )}

        {/* Pending Requests Table */}
        {activeTab === 0 && (
          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell>Staff Member</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Request Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Impact/Effect</TableCell>
                    <TableCell>Request Date</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <Typography variant="body1" color="textSecondary">No pending requests</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((request, idx) => {
                      const config = getRequestTypeConfig(request);
                      const amount = config.amount || 0;
                      
                      return (
                        <TableRow key={`${request.type}-${request.id}-${idx}`} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ bgcolor: config.color, width: 40, height: 40 }}>{config.icon}</Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">{request.full_name || 'N/A'}</Typography>
                                <Typography variant="caption" color="textSecondary">{request.phone || request.email}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={request.department || 'N/A'} variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={config.label} 
                              size="small" 
                              sx={{ bgcolor: config.color, color: 'white', fontWeight: 'bold' }} 
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" sx={{ color: config.color }}>
                              {request.type === 'deduction' ? '-' : '+'}{formatCurrency(amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={config.reason || 'No reason provided'}>
                              <Chip 
                                size="small" 
                                label={config.reason ? (config.reason.length > 30 ? config.reason.substring(0, 27) + '...' : config.reason) : 'No reason'}
                                variant="outlined"
                                icon={<CommentIcon sx={{ fontSize: 14 }} />}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              size="small" 
                              label={config.effect}
                              variant="outlined"
                              icon={<InfoIcon sx={{ fontSize: 14 }} />}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {formatDate(request.date_created || request.request_date)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Approve">
                              <IconButton 
                                size="small" 
                                onClick={() => { setSelectedRequest(request); setApproveDialogOpen(true); }}
                                sx={{ color: '#4caf50' }}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton 
                                size="small" 
                                onClick={() => { setSelectedRequest(request); setRejectDialogOpen(true); }}
                                sx={{ color: '#f44336' }}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredRequests.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Paper>
        )}

        {/* Approved Bonuses Table - FIXED */}
        {activeTab === 1 && (
          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Alert severity="info" sx={{ m: 2, borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Approved Bonuses Waiting for Payment:</strong> {approvedBonuses.length} bonus(es) totaling {formatCurrency(approvedBonuses.reduce((sum, b) => sum + (b.recent_bonus || b.bonus_amount || 0), 0))}
              </Typography>
            </Alert>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: '#fff3e0' }}>
                  <TableRow>
                    <TableCell>Staff Member</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell align="right">Bonus Amount</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Approved By</TableCell>
                    <TableCell>Approved Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {approvedBonuses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <Typography variant="body1" color="textSecondary">No approved bonuses awaiting payment</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    approvedBonuses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((bonus, idx) => (
                      <TableRow key={bonus.id || idx} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: '#ff9800', width: 40, height: 40 }}><BonusIcon /></Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">{bonus.full_name || 'N/A'}</Typography>
                              <Typography variant="caption" color="textSecondary">{bonus.phone || 'N/A'}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={bonus.department || 'N/A'} variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="#ff9800">
                            {formatCurrency(bonus.recent_bonus || bonus.bonus_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={bonus.bonus_reason || bonus.reason || 'No reason'}>
                            <Chip 
                              size="small" 
                              label={(bonus.bonus_reason || bonus.reason || 'No reason').substring(0, 30)} 
                              variant="outlined" 
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{bonus.approved_by || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{formatDate(bonus.approved_date)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            size="small" 
                            label="Approved & Unpaid"
                            sx={{ bgcolor: '#fff3e0', color: '#ed6c02', fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PaidIcon />}
                            onClick={() => handleMarkBonusPaid(bonus)}
                            sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
                          >
                            Mark as Paid
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={approvedBonuses.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Paper>
        )}

        {/* Deduction Register Table */}
        {activeTab === 2 && (
          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {registerLoading && <LinearProgress />}
            
            {/* Register Filters */}
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Search by name, department, or remark..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                  sx={{ flex: 2, minWidth: 200 }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Month</InputLabel>
                  <Select value={registerMonth} onChange={(e) => { setRegisterMonth(e.target.value); setTimeout(fetchDeductionRegister, 100); }} label="Month">
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <MenuItem key={m} value={m}>{new Date(2000, m-1, 1).toLocaleString('default', { month: 'long' })}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Year</InputLabel>
                  <Select value={registerYear} onChange={(e) => { setRegisterYear(e.target.value); setTimeout(fetchDeductionRegister, 100); }} label="Year">
                    {[2023,2024,2025,2026,2027].map(y => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Deduction Type</InputLabel>
                  <Select value={registerTypeFilter} onChange={(e) => { setRegisterTypeFilter(e.target.value); setTimeout(fetchDeductionRegister, 100); }} label="Deduction Type">
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="regular">Regular Deductions</MenuItem>
                    <MenuItem value="bonus">Bonuses</MenuItem>
                    <MenuItem value="statutory">Statutory Deductions</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select value={registerStatusFilter} onChange={(e) => { setRegisterStatusFilter(e.target.value); setTimeout(fetchDeductionRegister, 100); }} label="Status">
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
                <Button size="small" variant="outlined" onClick={fetchDeductionRegister}>
                  Refresh Register
                </Button>
              </Box>
              {registerStats && (
                <Box sx={{ mt: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Chip label={`Total Approved: ${formatCurrency(registerStats.total_approved)}`} color="success" size="small" />
                  <Chip label={`Total Pending: ${formatCurrency(registerStats.total_pending)}`} color="warning" size="small" />
                  <Chip label={`Regular: ${registerStats.regular_count}`} size="small" />
                  <Chip label={`Bonuses: ${registerStats.bonus_count}`} size="small" />
                  <Chip label={`Statutory: ${registerStats.statutory_count}`} size="small" />
                </Box>
              )}
            </Box>
            
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: '#e8f5e9' }}>
                  <TableRow>
                    <TableCell>Staff Member</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Deduction Category</TableCell>
                    <TableCell>Description / Reason</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Approved By</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRegister.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                        <Typography variant="body1" color="textSecondary">No deductions found for the selected period</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRegister.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((deduction, idx) => {
                      const config = getDeductionCategoryConfig(deduction.deduction_category);
                      const isApproved = deduction.deduction_status === 1 || deduction.deduction_status === 'approved';
                      const isPending = deduction.deduction_status === 0 || deduction.deduction_status === 'pending';
                      
                      return (
                        <TableRow key={`${deduction.deduction_category}-${deduction.id}-${idx}`} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ bgcolor: config.color, width: 32, height: 32 }}>{config.icon}</Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">{deduction.full_name}</Typography>
                                <Typography variant="caption" color="textSecondary">{deduction.phone}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={deduction.department || 'N/A'} variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={config.label} 
                              size="small" 
                              sx={{ bgcolor: config.bgColor, color: config.color, fontWeight: 'bold' }} 
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={deduction.remark || 'No description'}>
                              <Typography variant="body2" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {deduction.remark || 'N/A'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" sx={{ color: deduction.deduction_category === 'bonus' ? '#4caf50' : '#f44336' }}>
                              {deduction.deduction_category === 'bonus' ? '+' : '-'}{formatCurrency(deduction.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {deduction.payroll_month && deduction.payroll_year ? 
                                `${new Date(2000, deduction.payroll_month-1, 1).toLocaleString('default', { month: 'short' })} ${deduction.payroll_year}` : 
                                'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={deduction.status_text || (isApproved ? 'Approved' : isPending ? 'Pending' : 'Rejected')}
                              size="small"
                              sx={{ 
                                bgcolor: isApproved ? '#e8f5e9' : isPending ? '#fff3e0' : '#ffebee',
                                color: isApproved ? '#2e7d32' : isPending ? '#ed6c02' : '#c62828',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{deduction.approved_by || 'System'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {formatDate(deduction.approved_date || deduction.date_created)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredRegister.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
            />
          </Paper>
        )}

        <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
          <Typography variant="body2" color="textSecondary">Designed by Elegantè IT</Typography>
        </footer>
      </Box>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: selectedRequest ? getRequestTypeConfig(selectedRequest).color : '#4caf50', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon /> 
            Approve {selectedRequest ? getRequestTypeConfig(selectedRequest).label : 'Request'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {selectedRequest && (() => {
              const config = getRequestTypeConfig(selectedRequest);
              const amount = config.amount || 0;
              
              return (
                <>
                  <Alert severity="info">
                    <strong>Staff:</strong> {selectedRequest.full_name || selectedRequest.employee_name}<br />
                    <strong>Department:</strong> {selectedRequest.department}<br />
                    <strong>Request Type:</strong> {config.label}<br />
                    <strong>Amount:</strong> {formatCurrency(amount)}<br />
                    <strong>Effect:</strong> {config.effect}<br />
                  </Alert>
                  {config.reason && (
                    <Alert severity="warning">
                      <strong>Reason:</strong> {config.reason}
                    </Alert>
                  )}
                  <Alert severity="info">
                    <strong>Approving Officer:</strong> {currentUser.name} ({currentUser.role})
                  </Alert>
                  <TextField 
                    label="Approval Comment (Optional)" 
                    multiline 
                    rows={3} 
                    fullWidth 
                    value={approvalComment} 
                    onChange={(e) => setApprovalComment(sanitizeInput(e.target.value))} 
                    placeholder="Add any comments about this approval..." 
                  />
                </>
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              if (selectedRequest?.type === 'deduction') handleApproveDeduction();
              else if (selectedRequest?.type === 'bonus') handleApproveBonus();
              else handleApproveSalaryChange();
            }} 
            variant="contained" 
            sx={{ bgcolor: selectedRequest ? getRequestTypeConfig(selectedRequest).color : '#4caf50' }}
          >
            Confirm Approval
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f44336', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon /> 
            Reject {selectedRequest ? getRequestTypeConfig(selectedRequest).label : 'Request'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {selectedRequest && (() => {
              const config = getRequestTypeConfig(selectedRequest);
              const amount = config.amount || 0;
              
              return (
                <>
                  <Alert severity="warning">
                    <strong>Staff:</strong> {selectedRequest.full_name || selectedRequest.employee_name}<br />
                    <strong>Request Type:</strong> {config.label}<br />
                    <strong>Amount:</strong> {formatCurrency(amount)}<br />
                    {config.reason && <><strong>Reason:</strong> {config.reason}<br /></>}
                  </Alert>
                  <Alert severity="info">
                    <strong>Rejecting Officer:</strong> {currentUser.name} ({currentUser.role})
                  </Alert>
                  <TextField 
                    label="Rejection Reason" 
                    multiline 
                    rows={3} 
                    fullWidth 
                    required 
                    value={approvalComment} 
                    onChange={(e) => setApprovalComment(sanitizeInput(e.target.value))} 
                    placeholder="Please provide a reason for rejection..." 
                  />
                </>
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRejectRequest} variant="contained" sx={{ bgcolor: '#f44336' }}>Confirm Rejection</Button>
        </DialogActions>
      </Dialog>

      {/* Statutory Deductions Dialog */}
      <Dialog open={statutoryDialogOpen} onClose={() => setStatutoryDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: '#2196f3', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TaxIcon /> 
            Nigerian Statutory / National Deductions
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Alert severity="info">
              <Typography variant="body2" fontWeight="bold">Nigerian Statutory Deductions include:</Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>PAYE Tax (Progressive: 7% - 24% based on income bands)</li>
                <li>NHF - National Housing Fund (2.5% of salary)</li>
                <li>NSITF - Employee Compensation (1% of salary)</li>
                <li>Pension Contribution (8% of salary minimum)</li>
                <li>NHIS - National Health Insurance (5% of salary)</li>
                <li>Group Life Insurance (0.5% of salary)</li>
              </ul>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Payroll Month</InputLabel>
                <Select value={statutoryMonth} onChange={(e) => setStatutoryMonth(e.target.value)} label="Payroll Month">
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                    <MenuItem key={m} value={m}>{new Date(2000, m-1, 1).toLocaleString('default', { month: 'long' })}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Year</InputLabel>
                <Select value={statutoryYear} onChange={(e) => setStatutoryYear(e.target.value)} label="Year">
                  {[2023,2024,2025,2026,2027].map(y => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Apply To:</Typography>
              <RadioGroup row value={applyToAll ? 'all' : 'single'} onChange={(e) => {
                setApplyToAll(e.target.value === 'all');
                if (e.target.value === 'all') {
                  setStatutoryEmployee(null);
                  setEmployeeSearchTerm('');
                  setEmployeeSearchResults([]);
                  setCalculatedStatutory(null);
                }
              }}>
                <FormControlLabel value="single" control={<Radio />} label="Specific Employee" />
                <FormControlLabel value="all" control={<Radio />} label="All Active Employees" />
              </RadioGroup>
            </Box>

            {!applyToAll && (
              <>
                <Autocomplete
                  freeSolo
                  options={employeeSearchResults}
                  getOptionLabel={(option) => typeof option === 'string' ? option : `${option.surname} ${option.other_names} (${option.phone})`}
                  onInputChange={(event, newValue) => {
                    setEmployeeSearchTerm(newValue);
                    if (typeof newValue !== 'string' || !newValue) {
                      setStatutoryEmployee(null);
                      setCalculatedStatutory(null);
                    }
                  }}
                  onChange={(event, newValue) => {
                    if (newValue && typeof newValue !== 'string') {
                      setStatutoryEmployee(newValue);
                      setEmployeeSearchTerm(`${newValue.surname} ${newValue.other_names}`);
                    }
                  }}
                  loading={employeeSearchLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Employee by Name or Phone"
                      placeholder="Type at least 2 characters..."
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <PersonIcon />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">{option.surname} {option.other_names}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {option.department} | {option.phone} | Salary: {formatCurrency(option.monthly_salary)}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
                
                {statutoryEmployee && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Selected: <strong>{statutoryEmployee.surname} {statutoryEmployee.other_names}</strong> - {statutoryEmployee.department}
                    <br />
                    Monthly Salary: {formatCurrency(statutoryEmployee.monthly_salary)}
                  </Alert>
                )}
              </>
            )}

            <Divider />
            
            <Typography variant="subtitle1" fontWeight="bold">Select Deductions to Apply:</Typography>
            
            <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
              <Grid container spacing={2}>
                {availableDeductions.map((deduction) => (
                  <Grid item xs={12} key={deduction.id}>
                    <Card variant="outlined" sx={{ 
                      bgcolor: selectedDeductionIds.includes(deduction.id) ? '#e3f2fd' : 'white',
                      border: selectedDeductionIds.includes(deduction.id) ? '1px solid #2196f3' : '1px solid #e0e0e0'
                    }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                          <Checkbox
                            checked={selectedDeductionIds.includes(deduction.id)}
                            onChange={() => toggleDeductionSelection(deduction.id)}
                            disabled={deduction.is_mandatory === 1}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight="bold">
                              {deduction.deduction_name}
                              {deduction.is_mandatory === 1 && (
                                <Chip label="Mandatory" size="small" color="error" sx={{ ml: 1 }} />
                              )}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {deduction.deduction_type} • {deduction.calculation_type === 'percentage' ? `${deduction.base_rate}% of salary` : `Fixed amount`}
                              {deduction.min_amount > 0 && ` • Min: ₦${deduction.min_amount.toLocaleString()}`}
                              {deduction.max_amount > 0 && ` • Max: ₦${deduction.max_amount.toLocaleString()}`}
                            </Typography>
                            {deduction.description && (
                              <Typography variant="caption" display="block" color="textSecondary">
                                {deduction.description}
                              </Typography>
                            )}
                          </Box>
                          {selectedDeductionIds.includes(deduction.id) && (
                            <TextField
                              size="small"
                              label="Custom Amount"
                              type="number"
                              placeholder="Leave empty for auto-calculation"
                              value={customDeductionAmounts[deduction.id] || ''}
                              onChange={(e) => updateCustomDeductionAmount(deduction.id, e.target.value)}
                              sx={{ width: 180 }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                              }}
                            />
                          )}
                          {selectedDeductionIds.includes(deduction.id) && calculatedStatutory?.statutory_deductions && (
                            <Chip 
                              label={formatCurrency(calculatedStatutory.statutory_deductions.find(d => d.id === deduction.id)?.amount || 0)}
                              size="small"
                              color="primary"
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {calculatingDeductions && <LinearProgress sx={{ mt: 2 }} />}

            {calculatedStatutory && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">
                    Deduction Summary for {calculatedStatutory.employee_name}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                        <TableRow>
                          <TableCell>Deduction Name</TableCell>
                          <TableCell align="center">Type</TableCell>
                          <TableCell align="right">Rate</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {calculatedStatutory.statutory_deductions?.map((ded, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{ded.name}</TableCell>
                            <TableCell align="center">
                              <Chip size="small" label={ded.type} variant="outlined" />
                            </TableCell>
                            <TableCell align="right">
                              {ded.calculation_type === 'percentage' ? `${ded.rate}%` : 'Fixed'}
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={ded.amount}
                                onChange={(e) => updateCustomDeductionAmount(ded.id, e.target.value)}
                                sx={{ width: 120 }}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton size="small" color="error" onClick={() => {
                                updateCustomDeductionAmount(ded.id, 0);
                              }}>
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                          <TableCell colSpan={3}><strong>Total Statutory Deductions</strong></TableCell>
                          <TableCell align="right"><strong>{formatCurrency(calculatedStatutory.total_statutory_deductions)}</strong></TableCell>
                          <TableCell />
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={3}>Monthly Salary</TableCell>
                          <TableCell align="right">{formatCurrency(calculatedStatutory.monthly_salary)}</TableCell>
                          <TableCell />
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={3}>Net After Statutory</TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold" color={calculatedStatutory.net_after_statutory > 0 ? 'green' : 'red'}>
                              {formatCurrency(calculatedStatutory.net_after_statutory)}
                            </Typography>
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}

            <Alert severity="warning">
              <Typography variant="body2">
                <strong>Important:</strong> These deductions will be recorded for the selected payroll period.
                You can edit the amount for each deduction before applying. Mandatory deductions are automatically selected.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatutoryDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={applySelectedStatutoryDeductions} 
            variant="contained" 
            startIcon={<CalculateIcon />}
            sx={{ bgcolor: '#2196f3' }}
            disabled={selectedDeductionIds.length === 0 || (!applyToAll && !statutoryEmployee)}
          >
            Apply Selected Deductions
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SalaryApprovalsDashboard;